import { useCallback, useEffect, useState } from 'react';
import { deleteQueueUrl, userQueuesUrl } from '../lib/apiUrls';
import { withFirebaseAuthorization } from '../lib/authFetch';

function useUserQueues(userId) {
  const [userQueues, setUserQueues] = useState([]);
  const [queuesStatus, setQueuesStatus] = useState({
    type: 'idle',
    message: 'Queue history is not loaded yet.',
  });
  const [deletingQueueId, setDeletingQueueId] = useState(null);

  const loadQueues = useCallback(async () => {
    if (!userId) {
      setQueuesStatus({ type: 'error', message: 'User is not signed in.' });
      return;
    }

    setQueuesStatus({ type: 'loading', message: 'Loading queue history...' });

    try {
      const response = await fetch(userQueuesUrl(userId), await withFirebaseAuthorization());
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Could not load queue history.');
      }

      const nextQueues = Array.isArray(payload)
        ? payload
        : payload.queues || payload.data?.queues || payload.data || [];

      setUserQueues(nextQueues);
      setQueuesStatus({
        type: 'success',
        message: `${nextQueues.length} queue item(s) loaded.`,
      });
    } catch (error) {
      setQueuesStatus({ type: 'error', message: error.message || 'Could not load queue history.' });
    }
  }, [userId]);

  useEffect(() => {
    loadQueues();
  }, [loadQueues]);

  async function handleDeleteQueue(queueId) {
    if (!queueId) {
      return;
    }

    setDeletingQueueId(queueId);

    try {
      const response = await fetch(deleteQueueUrl(queueId), await withFirebaseAuthorization({
        method: 'DELETE',
      }));
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || 'Could not delete queue item.');
      }

      await loadQueues();
    } catch (error) {
      setQueuesStatus({ type: 'error', message: error.message || 'Could not delete queue item.' });
    } finally {
      setDeletingQueueId(null);
    }
  }

  return {
    userQueues,
    queuesStatus,
    deletingQueueId,
    loadQueues,
    handleDeleteQueue,
  };
}

export default useUserQueues;
